import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { MovementPageCombinedState, MovementPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { MovementPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<MovementPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: MovementPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const MovementPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<MovementPageDataContextProvider pageData={pageData}>
			<TrackingContext properties={{ movement_id: pageData.data.movement.id }}>{children}</TrackingContext>
		</MovementPageDataContextProvider>
	);
};
